import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import CheckboxInputListItem from 'LuxKit/List/CheckboxInputListItem'
import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LoyaltyPointsSliderSearch from 'loyalty/components/LoyaltyPointsSliderSearch'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'

const SliderContainer = styled.div`
  margin-bottom: ${rem(16)};
  min-width: 0;
`

const ViewPriceInPointsCheckbox = styled(CheckboxInputListItem)`
  &&& {
    padding-left: 0;
  }
`

interface Props {
  cacheKey?: string
  skipBatchMaxCap?: boolean
  skipBurnExperiment?: boolean
  viewPriceInPoints: boolean
  onViewPriceInPointsChange: (viewPriceInPoints: boolean) => void
  compact?: boolean
}

function LoyaltySocietePointsFilterGroup({
  cacheKey,
  skipBatchMaxCap,
  skipBurnExperiment,
  viewPriceInPoints,
  onViewPriceInPointsChange,
  compact,
}: Props) {
  const intl = useIntl()
  const { setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()

  const clearViewPriceInPoints = useCallback(() => {
    onViewPriceInPointsChange(false)
  }, [onViewPriceInPointsChange])

  const handleViewPriceInPointsToggle = useCallback(() => {
    if (viewPriceInPoints) {
      clearViewPriceInPoints()
    } else {
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
      onViewPriceInPointsChange(true)
    }
  }, [viewPriceInPoints, clearViewPriceInPoints, onViewPriceInPointsChange, setSearchBurnEnabled, setPointsToBurn])

  const viewPriceInPointsLabel = intl.formatMessage({
    id: 'Pl7ew0Wylbx4ZUv7DdgF',
    defaultMessage: 'View Price in Points',
    description: 'Checkbox label to show experience search prices as Société points only',
  })

  const viewPriceInPointsItems = useMemo<Array<FilterPanelCheckItem>>(() => [{
    value: 'view-price-in-points',
    checked: viewPriceInPoints,
    label: viewPriceInPointsLabel,
  }], [viewPriceInPoints, viewPriceInPointsLabel])

  return (
    <FilterPanelFilterGroup
      compact={compact}
      title={<LoyaltyTierLabel kind="primary">{getLoyaltyProgramDisplayName()}</LoyaltyTierLabel>}
    >
      <SliderContainer>
        <LoyaltyPointsSliderSearch
          layout="filterPanel"
          cacheKey={cacheKey}
          skipBatchMaxCap={skipBatchMaxCap}
          skipBurnExperiment={skipBurnExperiment}
          onBurnEnabled={clearViewPriceInPoints}
          disabled={viewPriceInPoints}
        />
      </SliderContainer>
      {compact && (
        <FilterPanelCheckboxGroup
          name="viewPriceInPoints"
          items={viewPriceInPointsItems}
          onChange={handleViewPriceInPointsToggle}
        />
      )}
      {!compact && (
        <ViewPriceInPointsCheckbox
          inputPlacement="start"
          checked={viewPriceInPoints}
          onChange={handleViewPriceInPointsToggle}
          title={viewPriceInPointsLabel}
          size="medium"
        />
      )}
    </FilterPanelFilterGroup>
  )
}

export default LoyaltySocietePointsFilterGroup
